<template>
  <div class="rental-list">
    <div class="content">
      <!-- 筛选头部 -->
      <div class="list-head">
        <h1 class="list-title">
          {{ isEn && communityName ? toPinyin(communityName) : (communityName || $t('rental.title')) }}
        </h1>

        <div class="publish-btn" v-if="isLandlord" @click="goPublish">
          {{ $t('rental.publish') }}
        </div>
      </div>

      <div class="filter-bar">
        <div class="filter-cell" @click="showSortPopup = true">
          <van-icon class="cell-icon" :class="{ on: currentSort !== 'default' }" name="swap" />
          <div class="cell-text">
            <span class="cell-label">{{ $t('rental.sortLabel') }}</span>
            <span class="cell-value" :class="{ on: currentSort !== 'default' }">{{ currentSortLabel }}</span>
          </div>
          <van-icon class="cell-arrow" name="arrow-down" />
        </div>

        <div class="filter-divider"></div>

        <div class="filter-cell" @click="showFilterPopup = true">
          <div class="cell-text">
            <span class="cell-label">{{ $t('rental.filterLabel') }}</span>
            <div class="cell-values" v-if="selectedCount">
              <span class="cell-tag" v-for="(name, i) in previewFilters" :key="i">{{ name }}</span>
              <span class="cell-badge" v-if="selectedCount > previewFilters.length">+{{ selectedCount - previewFilters.length }}</span>
            </div>
            <span class="cell-value" v-else>{{ $t('rental.filterAll') }}</span>
          </div>
          <van-icon class="cell-arrow" name="arrow-down" />
        </div>
      </div>

      <van-popup v-model="showSortPopup" position="bottom" round>
        <div class="sort-sheet">
          <div class="sheet-title">{{ $t('rental.sortSheetTitle') }}</div>
          <div
            class="sort-option"
            :class="{ active: currentSort === item.value }"
            v-for="item in sortOptions"
            :key="item.value"
            @click="selectSort(item.value)"
          >
            <span>{{ item.label }}</span>
            <van-icon v-if="currentSort === item.value" name="success" />
          </div>
        </div>
      </van-popup>

      <van-popup v-model="showFilterPopup" position="bottom" round>
        <div class="filter-popup">
          <div class="popup-head">
            <span class="popup-title">{{ $t('rental.filterSheetTitle') }}</span>
            <span class="popup-clear" @click="clearFilter">{{ $t('rental.clear') }}</span>
          </div>

          <div class="filter-group" v-if="tagsDict.length">
            <div class="group-title">{{ $t('rental.tagTitle') }}</div>
            <div class="group-options">
              <span
                class="filter-option"
                :class="{ active: selectedTagIds.includes(String(item.value)) }"
                v-for="item in tagsDict"
                :key="item.value"
                @click="toggleTagFilter(item)"
              >{{ getTagName(item) }}</span>
            </div>
          </div>

          <div class="filter-group" v-if="roomTypesDict.length">
            <div class="group-title">{{ $t('rental.roomTypeTitle') }}</div>
            <div class="group-options">
              <span
                class="filter-option"
                :class="{ active: selectedHouseType === String(item.value) }"
                v-for="item in roomTypesDict"
                :key="item.value"
                @click="toggleHouseType(item)"
              >{{ getTagName(item) }}</span>
            </div>
          </div>

          <div class="filter-group" v-if="facilitiesDict.length">
            <div class="group-title">{{ $t('rental.facilityTitle') }}</div>
            <div class="group-options">
              <span
                class="filter-option"
                :class="{ active: selectedFacilityIds.includes(String(item.value)) }"
                v-for="item in facilitiesDict"
                :key="item.value"
                @click="toggleFacilityFilter(item)"
              >
                <IconPark v-if="getFacilityIcon(item)" class="option-icon" :type="getFacilityIcon(item)" theme="outline" size="16" :fill="selectedFacilityIds.includes(String(item.value)) ? '#1e5bff' : '#8a94a6'" />
                {{ getTagName(item) }}
              </span>
            </div>
          </div>

          <button class="popup-confirm" @click="applyFilter">{{ $t('rental.confirm') }}</button>
        </div>
      </van-popup>

      <!-- 扫码进入时无首页切换入口,补中英文切换按钮 -->
      <span v-if="!!$route.query.params" class="lang-switch" @click="onSwitchLang">
        <span v-if="isEn">中文</span>
        <span v-else>EN</span>
      </span>

      <!-- 房源列表 -->
      <van-list v-model="loading" :finished="finished" :finished-text="$t('common.noMore')" @load="loadData" :offset="100">
        <div class="rent-card" v-for="(item, index) in list" :key="item.id" @click="goInfo(item.id)">
          <div class="owner">
            <van-icon name="user-o" size="20" color="#1e5bff" />
            <span>{{ formatLandlord(item) }}</span>
          </div>

          <div class="phone-btn" @click.stop="callLandlord(item.landlordMobile)">
            <van-icon name="phone-o" />
            {{ $t('rental.callPhone') }}
          </div>

          <div class="house-name">
            {{ formatBuildingAddress(item.subareaName, item.buildingName, isEn) }}
          </div>

          <div class="house-desc">
            {{ isEn ? (item.descriptionEn || '') : (item.descriptionZh || '') }}
          </div>

          <div class="info-row" v-if="getAllTags(item).length">
            <span
              class="tag"
              v-for="(label, idx) in (expandedMap[index] || getAllTags(item).length <= 4) ? getAllTags(item) : getAllTags(item).slice(0, 4)"
              :key="idx"
            >{{ label }}</span>
            <span
              v-if="getAllTags(item).length > 4"
              class="more-btn"
              :class="{ on: expandedMap[index] }"
              @click.stop="toggleExpand(index)"
            >
              <template v-if="expandedMap[index]">
                {{ $t('rental.collapse') }}
                <van-icon name="arrow-up" />
              </template>
              <template v-else>
                +{{ getAllTags(item).length - 4 }}
                <van-icon name="arrow-down" />
              </template>
            </span>
          </div>

          <div class="time">
            <van-icon name="clock-o" />
            {{ item.updateTime }}
          </div>

          <div class="price">
            {{ formatPrice(item) }}
          </div>
        </div>
      </van-list>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { pinyin } from 'pinyin-pro'
import { IconPark } from '@icon-park/vue/es/all'
import { getImageStream } from '@/utils/index'
import { toPinyin, formatBuildingAddress } from './addressUtil'

const compoundSurnames =[
  '欧阳', '司马', '上官', '诸葛', '东方', '独孤', '南宫', '夏侯',
  '尉迟', '皇甫', '慕容', '宇文', '令狐', '长孙', '公孙', '呼延',
  '司徒', '拓跋', '端木', '申屠', '万俟', '闻人', '赫连', '澹台',
  '公羊', '宗政', '濮阳', '太史', '闾丘', '轩辕', '巫马', '乐正',
  '公冶', '漆雕',
]

function getSurname(name) {
  for (const cs of compoundSurnames) {
    if (name.startsWith(cs)) {
      const py = pinyin(cs, { toneType: 'none', type: 'array' })
      return { pinyin: py.join(''), chinese: cs }
    }
  }
  const first = name.charAt(0)
  const py = pinyin(first, { toneType: 'none', type: 'array' })
  return { pinyin: py[0], chinese: first }
}


export default {
  name: 'saveCode-rental',
  data() {
    return {
      loading: true,
      finished: false,
      page: 1,
      limit: 10,
      currentSort: 'default',
      showSortPopup: false,
      showFilterPopup: false,
      selectedHouseType: null,
      selectedFacilityIds: [],
      selectedTagIds: [],
      list: [],
      isLandlord: false,
      expandedMap: {},
      community: '',
      communityName: '',
      street: '',
      subarea: '',
      roomTypesDict: [],
      facilitiesDict: [],
      tagsDict: []
    }
  },
  created() {
    this.handleDomain()
    this.subarea = this.$route.query.subarea || ''
    this.loadRoomTypes()
    this.loadFacilities()
    this.loadTags()
    var qrParams = this.$route.query.params
    if (qrParams) {
      this.loadFromQrCode(qrParams)
    } else {
      this.community = this.$orgId || ''
      this.street = this.$globalData && this.$globalData.userInfo ? this.$globalData.userInfo.street || '' : ''
      this.loading = false
    }
  },
  computed: {
    isEn() {
      return Vue.config.lang === 'en-US'
    },
    sortOptions() {
      return [
        { label: this.$t('rental.sortDefault'), value: 'default' },
        { label: this.$t('rental.sortPrice'), value: 'price' },
        { label: this.$t('rental.sortTime'), value: 'time' }
      ]
    },
    currentSortLabel() {
      const item = this.sortOptions.find(option => option.value === this.currentSort)
      return item ? item.label : ''
    },
    selectedCount() {
      return (this.selectedHouseType ? 1 : 0) + this.selectedFacilityIds.length + this.selectedTagIds.length
    },
    previewFilters() {
      const names = []
      const pushName = (item) => {
        if (item && names.length < 2) names.push(this.getTagName(item))
      }
      if (this.selectedHouseType) {
        pushName(this.roomTypesDict.find(item => String(item.value) === this.selectedHouseType))
      }
      this.selectedFacilityIds
        .map(id => this.facilitiesDict.find(item => String(item.value) === id))
        .forEach(pushName)
      this.selectedTagIds
        .map(id => this.tagsDict.find(item => String(item.value) === id))
        .forEach(pushName)
      return names
    }
  },
  methods: {
    toPinyin,
    formatBuildingAddress,
    handleDomain() {
      var domain = this.$route.query.domain
      if (domain && domain !== 'null' && domain !== '0' && domain !== 'undefined') {
        if (domain === 'https://sgg.hzxh.gov.cn') {
          domain = 'https://shgz.hzxh.gov.cn'
          this.$route.query.domain = domain
        }
        Vue.prototype.$globalData.domain = decodeURIComponent(domain)
        this.$http.changeUrl(domain, true)
        this.$httpApp.changeUrl(domain, true)
        this.$httpCustApp.changeUrl(domain, true)
      }
    },
    loadFromQrCode(id) {
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/scanCode/getScanCodeParams'),
        method: 'get',
        params: this.$httpCustApp.adornParams({ id })
      }).then(({ data }) => {
        if (data && data.code === 0) {
          var orgType = data.data.orgType
          var orgId = data.data.orgId
          if (orgType === 'street') {
            this.street = orgId
          } else if (orgType === 'community') {
            this.community = orgId
          } else if (orgType === 'subarea') {
            this.subarea = orgId
          }
          this.loading = false
        }
      }).catch(() => {
        this.$toast.fail(this.$t('common.loadFail'))
      })
    },
    loadData() {
      if (this.finished) return

      this.$toast.loading({ duration: 0, message: this.$t('common.loading'), forbidClick: true })

      const isQrCode = !!this.$route.query.params
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl(isQrCode ? '/wxapp/landlord/listing/publicList' : '/wxapp/landlord/listing/list'),
        method: 'get',
        params: this.$httpCustApp.adornParams({
          page: this.page,
          limit: this.limit,
          sort: this.currentSort,
          community: this.community,
          street: this.street,
          subarea: this.subarea || null,
          houseType: this.selectedHouseType || undefined,
          facilityIds: this.selectedFacilityIds.length ? this.selectedFacilityIds.join(',') : undefined,
          tagIds: this.selectedTagIds.length ? this.selectedTagIds.join(',') : undefined,
          // 仅英文环境要求已翻译(description_en 非空)的房源;中文环境不过滤,全部上架房源可见
          bilingual: this.isEn ? true : undefined,
          status: 1
        })
      }).then(({ data }) => {
        this.$toast.clear()
        this.loading = false

        if (data.code === 0) {
          const pageData = data.page || {}
          const list = pageData.list || []

          const records = list.map(item => {
            let images = []
            try {
              images = typeof item.images === 'string' ? JSON.parse(item.images) : (item.images || [])
            } catch (e) {
              images = []
            }
            return {
              ...item,
              address: item.address || '',
              landlordName: item.landlordName || '',
              imagePath: images.length ? getImageStream(images[0]) : ''
            }
          })

          this.list = this.page === 1 ? records : [...this.list, ...records]

          if (this.page === 1 && records.length) {
            this.communityName = records[0].communityName || ''
          }

          if (!list.length || list.length < this.limit) {
            this.finished = true
          }

          if (this.page === 1) {
            this.isLandlord = data.isLandlord !== undefined ? data.isLandlord : true
          }

          this.page++
        } else {
          this.finished = true
          this.$toast.fail({ duration: 1500, forbidClick: true, message: data.msg || this.$t('common.loadFail') })
        }
      }).catch(() => {
        this.$toast.clear()
        this.loading = false
        this.finished = true
      })
    },
    resetAndLoad() {
      this.page = 1
      this.list = []
      this.expandedMap = {}
      this.finished = false
      this.loading = true
      this.loadData()
    },
    selectSort(value) {
      this.currentSort = value
      this.showSortPopup = false
      this.resetAndLoad()
    },
    onSwitchLang() {
      this.$switchLang()
      // 语言影响 bilingual 过滤和标签/房型/设施显示名,需用新语言重新查询
      this.resetAndLoad()
    },
    toggleHouseType(item) {
      const id = String(item.value)
      this.selectedHouseType = this.selectedHouseType === id ? null : id
    },
    toggleFacilityFilter(item) {
      const id = String(item.value)
      const index = this.selectedFacilityIds.indexOf(id)
      if (index > -1) {
        this.selectedFacilityIds.splice(index, 1)
      } else {
        this.selectedFacilityIds.push(id)
      }
    },
    toggleTagFilter(item) {
      const id = String(item.value)
      const index = this.selectedTagIds.indexOf(id)
      if (index > -1) {
        this.selectedTagIds.splice(index, 1)
      } else {
        this.selectedTagIds.push(id)
      }
    },
    clearFilter() {
      this.selectedHouseType = null
      this.selectedFacilityIds = []
      this.selectedTagIds = []
      this.showFilterPopup = false
      this.resetAndLoad()
    },
    applyFilter() {
      this.showFilterPopup = false
      this.resetAndLoad()
    },
    formatLandlord(item) {
      const name = (item.landlordName || '').trim()
      if (!name) return ''
      const gender = item.landlordGender
      const surname = getSurname(name)
      const display = this.isEn
        ? surname.pinyin.charAt(0).toUpperCase() + surname.pinyin.slice(1)
        : surname.chinese
      if (gender === 1) return this.$t('rental.mr', { surname: display })
      if (gender === 2) return this.$t('rental.ms', { surname: display })
      return ''
    },
    loadRoomTypes() {
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/sys/dict/listDictByCode'),
        method: 'get',
        params: this.$httpCustApp.adornParams({ code: 'landlord_house_type' })
      }).then(({ data }) => {
        if (data.code === 0) {
          this.roomTypesDict = data.dicts || []
        }
      }).catch(() => {
      })
    },
    loadFacilities() {
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/sys/dict/listDictByCode'),
        method: 'get',
        params: this.$httpCustApp.adornParams({ code: 'landlord_facility' })
      }).then(({ data }) => {
        if (data.code === 0) {
          this.facilitiesDict = data.dicts || []
        }
      }).catch(() => {
      })
    },
    getFacilityIcon(item) {
      const ext = item.extended
      if (!ext) return ''
      try {
        const parsed = JSON.parse(ext)
        return (parsed && parsed.icon) || ''
      } catch (e) {
        return typeof ext === 'string' ? ext : ''
      }
    },
    loadTags() {
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/sys/dict/listDictByCode'),
        method: 'get',
        params: this.$httpCustApp.adornParams({ code: 'landlord_tag' })
      }).then(({ data }) => {
        if (data.code === 0) {
          this.tagsDict = data.dicts || []
        }
      })
    },
    getTagName(item) {
      if (this.isEn && item.extended) {
        try {
          const ext = JSON.parse(item.extended)
          if (ext.en) return ext.en
        } catch (e) {}
      }
      return this.isEn ? 'Translating...' : item.label
    },
    getTagLabels(item) {
      if (!item.tagIds) return []
      let ids = item.tagIds
      if (typeof ids === 'string') {
        try {
          ids = JSON.parse(ids)
        } catch (e) {
          ids = ids.split(',').map(id => id.trim())
        }
      }
      const selectedIds = new Set(ids.map(id => String(id)))
      return this.tagsDict
        .filter(dict => selectedIds.has(String(dict.value)))
        .map(dict => this.getTagName(dict))
    },
    getAllTags(item) {
      const result = []
      if (this.isEn) {
        result.push(item.houseTypeNameEn || 'Translating...')
      } else if (item.houseTypeName) {
        result.push(item.houseTypeName)
      }
      const tags = this.getTagLabels(item)
      if (tags.length) result.push(...tags)
      return result
    },
    toggleExpand(index) {
      this.$set(this.expandedMap, index, !this.expandedMap[index])
    },
    formatPrice(item) {
      if (item.minRent && item.maxRent) return this.$t('rental.priceRange', { min: item.minRent, max: item.maxRent })
      if (item.minRent) return this.$t('rental.priceFrom', { price: item.minRent })
      return item.maxRent || ''
    },
    goInfo(id) {
      this.$router.push({ path: '/saveCode-rental-info', query: { id } })
    },
    goPublish() {
      this.$router.push('/saveCode-rental-publish')
    },
    callLandlord(phone) {
      if (phone) {
        window.location.href = 'tel:' + phone
      }
    }
  }
}
</script>

<style scoped lang="scss">
.rental-list {
  min-height: 100vh;
  background: linear-gradient(180deg,
      #c3d2f9 0%,
      #eef4ff 48%,
      #ffffff 100%);


  .content {
    padding: 29px;
  }

  .list-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
  }

  .list-title {
    margin: 0;
    color: #1e5bff;
    font-size: 40px;
    font-weight: 800;
  }

  .publish-btn {
    width: 158px;
    height: 65px;

    border-radius: 22px;

    display: flex;
    justify-content: center;
    align-items: center;

    background: linear-gradient(116deg,
        #f9a01b 20%,
        #f5c33b 94%);

    color: #fff;
    font-size: 25px;
    font-weight: 800;
  }

  .filter-bar {
    height: 84px;
    display: flex;
    align-items: center;
    padding: 0 10px;
    margin-bottom: 22px;
    background: rgba(255, 255, 255, .92);
    border-radius: 22px;
    box-shadow: 0 4px 12px rgba(15, 40, 90, .06);
  }

  .filter-cell {
    flex: 1;
    height: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 14px;
  }

  .cell-icon {
    flex-shrink: 0;
    font-size: 30px;
    color: #9aa7bd;

    &.on {
      color: #1e5bff;
    }
  }

  .cell-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .cell-label {
    font-size: 20px;
    line-height: 1;
    letter-spacing: 1px;
    color: #9aa7bd;
  }

  .cell-value {
    font-size: 26px;
    font-weight: 700;
    color: #0b1f3f;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.on {
      color: #1e5bff;
    }
  }

  .cell-values {
    width: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .cell-tag {
    max-width: 100px;
    min-width: 0;
    height: 38px;
    line-height: 38px;
    padding: 0 14px;
    border-radius: 12px;
    background: #e9f0ff;
    color: #1e5bff;
    font-size: 20px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .cell-badge {
    flex-shrink: 0;
    height: 36px;
    line-height: 36px;
    padding: 0 12px;
    border-radius: 18px;
    background: #1e5bff;
    color: #fff;
    font-size: 20px;
    font-weight: 800;
  }

  .cell-arrow {
    flex-shrink: 0;
    font-size: 24px;
    color: #c3cede;
  }

  .filter-divider {
    flex-shrink: 0;
    width: 1px;
    height: 40px;
    background: #e8edf6;
  }

  .sort-sheet {
    padding: 30px 30px calc(40px + env(safe-area-inset-bottom));
    background: #fff;
  }

  .sheet-title {
    margin-bottom: 16px;
    font-size: 30px;
    font-weight: 800;
    color: #0b1f3f;
  }

  .sort-option {
    height: 92px;
    margin-bottom: 14px;
    padding: 0 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 18px;
    background: #f7f9fc;
    font-size: 28px;
    color: #0b1f3f;

    &.active {
      background: #e9f0ff;
      color: #1e5bff;
      font-weight: 800;
    }
  }

  .filter-popup {
    max-height: 78vh;
    overflow-y: auto;
    padding: 28px 30px calc(40px + env(safe-area-inset-bottom));
    background: #fff;
  }

  .popup-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 26px;
  }

  .popup-title {
    font-size: 30px;
    font-weight: 800;
    color: #0b1f3f;
  }

  .popup-clear {
    font-size: 24px;
    color: #1e5bff;
  }

  .filter-group {
    margin-bottom: 26px;
  }

  .group-title {
    margin-bottom: 16px;
    font-size: 26px;
    font-weight: 800;
    color: #0b1f3f;
  }

  .group-options {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .filter-option {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 56px;
    line-height: 1;
    padding: 0 24px;
    border-radius: 28px;
    background: #f3f6fb;
    color: #555;
    font-size: 24px;

    &.active {
      background: #e9f0ff;
      color: #1e5bff;
      font-weight: 800;
    }
  }

  .option-icon {
    flex-shrink: 0;
  }

  .popup-confirm {
    width: 100%;
    height: 78px;
    margin-top: 30px;
    border: 0;
    border-radius: 24px;
    background: #1e5bff;
    color: #fff;
    font-size: 28px;
    font-weight: 800;
  }

  .rent-card {
    position: relative;

    min-height: 299px;

    background: #fff;
    border-radius: 29px;

    margin-bottom: 25px;

    padding: 25px 22px 90px;

    box-shadow: 0 7px 14px rgba(0, 0, 0, .12);
  }

  .owner {
    display: flex;
    align-items: center;
    gap: 14px;

    color: #1e5bff;
    font-size: 25px;
  }

  .phone-btn {
    position: absolute;

    top: 27px;
    right: 29px;

    width: 180px;
    height: 54px;

    border: 2px solid #1e5bff;
    border-radius: 14px;

    display: flex;
    align-items: center;
    justify-content: center;

    gap: 7px;

    color: #1e5bff;
    background: #fff;

    font-size: 23px;
    font-weight: 700;
  }

  .house-name {
    margin-top: 28px;

    font-size: 36px;
    font-weight: 800;

    color: #0b1f3f;
  }

  .house-desc {
    margin-top: 18px;

    font-size: 25px;
    color: #555;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .info-row {
    margin-top: 14px;

    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 14px;

    .more-btn {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      height: 39px;
      padding: 0 14px;
      border-radius: 11px;
      border: 1.5px solid #c6d0e0;
      background: transparent;
      color: #8a94a6;
      font-size: 22px;
      font-weight: 500;
      white-space: nowrap;

      .van-icon {
        font-size: 14px;
      }

      &.on {
        border-color: #1e5bff;
        color: #1e5bff;
        font-weight: 800;
      }
    }
  }

  .tag {
    height: 39px;
    line-height: 39px;

    padding: 0 14px;

    border-radius: 11px;

    background: #d9e4ff;

    color: #1e5bff;

    font-size: 22px;
    font-weight: 500;
    white-space: nowrap;
  }

  .time {
    position: absolute;

    left: 22px;
    bottom: 25px;

    display: flex;
    align-items: center;
    gap: 7px;

    color: #9b9b9b;

    font-size: 22px;
  }

  .price {
    position: absolute;

    right: 29px;
    bottom: 18px;

    color: #f7941e;

    font-size: 40px;
    font-weight: 800;
  }

  .lang-switch {
    position: fixed;
    bottom: 160px;
    right: 30px;
    z-index: 50;
    background: #4080ff;
    color: #fff;
    padding: 12px 28px;
    border-radius: 32px;
    font-size: 24px;
    font-weight: 500;
    box-shadow: 0 4px 16px rgba(64, 128, 255, 0.35);

    &:active {
      opacity: 0.8;
    }
  }

}
</style>
